/**
 * Formats a date string or object into a localized string with timezone support.
 * @param date - The date to format (string or Date object)
 * @param timezone - The timezone to use for formatting (optional, defaults to browser local if not provided or invalid)
 * @returns Formatted date string including short timezone name
 */
export const formatDateWithTimezone = (date: string | Date, timezone?: string): string => {
  try {
    return new Date(date).toLocaleString(undefined, {
      timeZone: timezone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      timeZoneName: 'short',
    });
  } catch (error) {
    // Fallback to local time if timezone is invalid
    console.warn(`Invalid timezone: ${timezone}, falling back to local time.`);
    return new Date(date).toLocaleString(undefined, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      timeZoneName: 'short',
    });
  }
};

/**
 * Formats hours as a readable string with hours and minutes.
 * @param hours - The number of hours (can be fractional)
 * @returns Formatted string (e.g., "2h", "2h 30m", or "-" for zero/falsy values)
 */
export const formatHours = (hours: number): string => {
  if (!hours) return '-';

  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);

  if (minutes === 0) {
    return `${wholeHours}h`;
  }

  return `${wholeHours}h ${minutes}m`;
};
